/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

// Material Dashboard 2 PRO React TS components
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React TS examples components
import SalesTableCell from "examples/Tables/SalesTable/SalesTableCell";

import { Icon, Tooltip } from "@mui/material";

// Declaring props types for SalesTable
type CellPrimitive = string | number;
type CellValue = CellPrimitive | CellPrimitive[];

interface Props {
  title?: string;
  rows?: Record<string, CellValue>[];
  shadow?: boolean;
  tooltipText?: string;
}

function stringify(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

function SalesTable({ title, rows = [], shadow, tooltipText }: Props): JSX.Element {
  const renderTableCells = useMemo(() => {
    return (rows ?? []).map((row, rowIndex) => {
      const isLastRow = rowIndex === (rows?.length ?? 0) - 1;

      const tableCells = Object.entries(row).map(([cellTitle, cellContent]) => {
        const cellKey = `cell-${rowIndex}-${cellTitle}`;

        // If the cell has an image + content pair, normalize both to string
        if (Array.isArray(cellContent)) {
          const image = stringify(cellContent[0]);
          const content = stringify(cellContent[1]);

          return (
            <SalesTableCell
              key={cellKey}
              title={stringify(cellTitle)}
              content={content}
              image={image}
              noBorder={isLastRow}
            />
          );
        }

        // Otherwise normalize the content to string (fixes number -> string TS error)
        return (
          <SalesTableCell
            key={cellKey}
            title={stringify(cellTitle)}
            content={stringify(cellContent)}
            noBorder={isLastRow}
          />
        );
      });

      return <TableRow key={`row-${rowIndex}`}>{tableCells}</TableRow>;
    });
  }, [rows]);

  return (
    <TableContainer sx={{ height: "100%", boxShadow: !shadow && "none" }}>
      <Table>
        {title ? (
          <TableHead>
            <MDBox component="tr" width="max-content" display="block" mb={1.5}>
              <MDTypography variant="h6" component="td" display="inline">
                {title}
              </MDTypography>

              {tooltipText ? (
                <Tooltip
                  enterTouchDelay={0}
                  leaveTouchDelay={5000}
                  sx={{ marginBottom: "-4px", marginLeft: "5px" }}
                  title={tooltipText}
                  placement="top"
                >
                  <Icon>info</Icon>
                </Tooltip>
              ) : null}
            </MDBox>
          </TableHead>
        ) : null}

        <TableBody>{renderTableCells}</TableBody>
      </Table>
    </TableContainer>
  );
}

// Declaring default props for SalesTable
SalesTable.defaultProps = {
  title: "",
  rows: [{}],
  shadow: true,
};

export default SalesTable;
